/**
 * "Preview as client" — an admin sees the public website's buyer or seller
 * portal exactly as that client would, READ-ONLY, including clients who have
 * never signed in (a `buyers`/`sellers` row with no login yet).
 *
 * How it differs from "Open portal" (admin-portal-login.ts): Open portal mints
 * the client's own kind of sign-in link and needs a claimed account, and the
 * session it opens can act. A preview needs no login at all, opens a separate
 * short-lived preview session bound to the admin AND the one client, and the
 * API refuses every write made under it.
 *
 * This module is the website-facing half (the `/auth/me` context, redeem and
 * end). The admin mint request/response live in admin-portal-login.ts.
 */

import { z } from 'zod';

/** The single-use link the dashboard opens. Clicked within seconds. */
export const PORTAL_PREVIEW_LINK_TTL_MINUTES = 5;

/**
 * How long a redeemed preview lasts. Fixed from the redeem — it never slides,
 * however busy the tab is. Ending it early is the banner's "End preview".
 */
export const PORTAL_PREVIEW_SESSION_MINUTES = 60;

/**
 * The preview credential's cookie. Deliberately NOT `yrp_session`: on a shared
 * host (localhost in development) a second `yrp_session` would replace the
 * admin's own session, and ending the preview would sign them out of the
 * dashboard (or the website's agent area).
 */
export const PORTAL_PREVIEW_COOKIE_NAME = 'yrp_preview';

/**
 * A non-secret companion cookie (not httpOnly, same lifetime) that lets the
 * website's client-side code switch analytics, the chat dock and activity
 * tracking off while a preview is open. It grants nothing on its own.
 */
export const PORTAL_PREVIEW_UI_COOKIE_NAME = 'yrp_preview_ui';

/**
 * The website stamps this header on every request it sends the API (its
 * middleware for proxied `/api/*` calls; its server-side fetches by hand).
 * The API reads a preview credential ONLY on a request carrying it, so a
 * `yrp_preview` cookie that reaches the dashboard (same host in development)
 * is ignored there, and a preview can only be redeemed through the website.
 */
export const PORTAL_PREVIEW_SURFACE_HEADER = 'x-yrp-surface';
export const PORTAL_PREVIEW_SURFACE_VALUE = 'web-public';

/** `details.reason` on the 403 every write gets while previewing. */
export const PORTAL_PREVIEW_READ_ONLY_REASON = 'preview_read_only';
/** `details.reason` on the 403 for a read outside the client portal (staff routes). */
export const PORTAL_PREVIEW_OUT_OF_SCOPE_REASON = 'preview_out_of_scope';

export const PORTAL_PREVIEW_READ_ONLY_MESSAGE =
  "This is a read-only preview. Nothing you do here is saved.";
export const PORTAL_PREVIEW_OUT_OF_SCOPE_MESSAGE =
  'A client preview can only open the client portal. End the preview to use anything else.';

export const PortalPreviewKind = z.enum(['buyer', 'seller']);
export type PortalPreviewKind = z.infer<typeof PortalPreviewKind>;

/** What the website needs to draw the preview banner. */
export const PortalPreviewContext = z.object({
  kind: PortalPreviewKind,
  /** Whose portal this is — the name on the buyer/seller row. */
  clientName: z.string(),
  /** False for a client who has never signed in (no login yet). */
  hasLogin: z.boolean(),
  /** The client's website access was revoked: they cannot see this themselves. */
  accessRevoked: z.boolean(),
  /** When the preview session ends on its own (ISO). */
  expiresAt: z.string(),
});
export type PortalPreviewContext = z.infer<typeof PortalPreviewContext>;

export const PortalPreviewRedeemResponse = z.object({
  preview: PortalPreviewContext,
  /** Where the website should land: the buyer or the seller portal. */
  redirectTo: z.string(),
});
export type PortalPreviewRedeemResponse = z.infer<typeof PortalPreviewRedeemResponse>;

export const PortalPreviewEndResponse = z.object({
  ok: z.literal(true),
  /** False when there was no live preview to end (already ended or expired). */
  ended: z.boolean(),
});
export type PortalPreviewEndResponse = z.infer<typeof PortalPreviewEndResponse>;

/**
 * Where a redeemed preview lands on the website. One portal shell at
 * `/account/` serves buyers and sellers alike (`/sell/dashboard/` redirects
 * there); the kind is kept so a future split needs no API change.
 */
export function portalPreviewLandingPath(_kind: PortalPreviewKind): string {
  return '/account/';
}

/** The banner sentence, kept here so every surface says the same thing. */
export function portalPreviewBannerText(preview: Pick<PortalPreviewContext, 'clientName'>): string {
  return `Previewing as ${preview.clientName} — read-only. Nothing you do here is saved.`;
}
